import React, {
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import { toast } from "react-toastify";
import API from "../Services/Report";
import APIS from "../Services/TaxRate";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";

const ProductCategory = forwardRef(
    ({ filters, selectedCenter, resetTrigger, applyTrigger, dateTrigger, radioFilter }, ref) => {
        const perPage = 10;
        const productLimit = 10;
        const [taxRate, setTaxRate] = useState([]);
        const [categoryPage, setCategoryPage] = useState(1);
        const [categoryTotalPages, setCategoryTotalPages] = useState(1);
        const [finance, setFinance] = useState([]);
        const [totalCount, setTotalCount] = useState(0);

        const [financeProduct, setFinanceProduct] = useState([]);
        const [productPage, setProductPage] = useState(1);
        const [productTotalPages, setProductTotalPages] = useState(1);
        const [productId, setProductId] = useState(null);

        const [columnTotals, setColumnTotals] = useState({
            netSale: 0,
            grossSale: 0,
            additionalCess: 0,
            skyCoins: 0,
            gstTotals: {}
        });

        const today = new Date();
        const formattedToday = today.toISOString().split("T")[0];
        const pastDate = new Date();
        pastDate.setDate(pastDate.getDate() - 90);
        const formattedPastDate = pastDate.toISOString().split("T")[0];


        const buildCenterList = () =>
            Array.isArray(selectedCenter) ? selectedCenter.join(",") : selectedCenter;

        const bindGetTaxtRate = async () => {
            try {
                const basePayload = { limit: 0, pageNumber: 1, is_recipe: 0 };
                const response = await APIS.getTaxRate(basePayload);
                const details = response?.data?.data;
                setTaxRate(details?.rows || []);
            } catch (error) {
                toast.dismiss();
                toast.error("Error fetching tax rates.");
                console.error("TaxRate error:", error);
            }
        };

        const getFinanceReport = async (page = 1) => {
            try {
                const centerlist = buildCenterList();
                const payloadData = {
                    start_date: filters?.startDate || formattedPastDate,
                    end_date: filters?.endDate || formattedToday,
                    location_id: centerlist && centerlist.length > 0 ? centerlist : null,
                    page_number: page,
                    limit: perPage,
                };

                const response = await API.addFinanceReport(payloadData);
                if (response?.status === 200 && Array.isArray(response?.data?.result)) {
                    const count = Number(response?.data?.total_count ?? 0);
                    setTotalCount(count);
                    setFinance(response.data.result || []);
                    setCategoryPage(page);
                    setCategoryTotalPages(Math.max(1, Math.ceil(count / perPage)));
                    calculateTotals(response.data.result);
                } else {
                    toast.error(response?.data?.message || "No finance report found");
                    setFinance([]);
                    setCategoryTotalPages(1);
                    setTotalCount(0);
                    setCategoryPage(1);
                }
            } catch (error) {
                toast.error("Something went wrong while fetching GST report");
                console.error("getFinanceReport error:", error);
                setFinance([]);
                setCategoryTotalPages(1);
                setTotalCount(0);
                setCategoryPage(1);
            }
        };

        const calculateTotals = (data) => {
            let netSaleSum = 0;
            let grossSaleSum = 0;
            let additionalCessSum = 0;
            let skyCoinsSum = 0;

            let gstTotals = {};
            data.forEach((item) => {
                netSaleSum += Number(item["Net Sale"]) || 0;
                grossSaleSum += Number(item["Gross Sale"]) || 0;
                additionalCessSum += Number(item["Additional Cess"]) || 0;
                skyCoinsSum += Number(item["SkyCoins Exhausted"]) || 0;

                Object.keys(item).forEach((key) => {
                    if (key.endsWith(" GST")) {
                        gstTotals[key] = (gstTotals[key] || 0) + Number(item[key] || 0);
                    }
                });
            });

            setColumnTotals({
                netSale: netSaleSum,
                gstTotals,
                grossSale: grossSaleSum,
                additionalCess: additionalCessSum,
                skyCoins: skyCoinsSum,
            });
        };
        const getFinanceProductId = async (page = 1) => {
            try {
                const centerlist = buildCenterList();
                const payload1 = {
                    start_date: filters?.startDate || formattedPastDate,
                    end_date: filters?.endDate || formattedToday,
                    location_id: centerlist && centerlist.length > 0 ? centerlist : null,
                    page_number: page,
                    limit: perPage,
                };

                const response = await API.getProductWiseGstReport(payload1);

                if (response?.status === 200 && Array.isArray(response?.data?.result)) {
                    const count = Number(response?.data?.total_count ?? 0);
                    setTotalCount(count);
                    setFinance(response.data.result || []);
                    setCategoryPage(page);
                    setCategoryTotalPages(Math.max(1, Math.ceil(count / perPage)));
                    calculateTotals(response.data.result);
                } else {
                    toast.error(response?.data?.message || "No product report found");
                    setFinance([]);
                    setCategoryPage(1);
                    setCategoryTotalPages(1);
                    setTotalCount(0);
                }
            } catch (error) {
                console.error("Error fetching product data:", error);
                toast.error("Failed to fetch product report");
                setFinance([]);
                setCategoryPage(1);
                setCategoryTotalPages(1);
                setTotalCount(0);
            }
        };

        const getFinanceProductByCategory = async (id, page = 1) => {
            try {
                setFinanceProduct([]);
                setProductId(id);

                const centerlist = buildCenterList();
                const payload1 = {
                    start_date: filters?.startDate || formattedPastDate,
                    end_date: filters?.endDate || formattedToday,
                    location_id: centerlist && centerlist.length > 0 ? centerlist : null,
                    product_type_id: id,
                    page_number: page,
                    limit: productLimit,
                };

                const response = await API.getFinanceProductType(payload1);

                if (response?.status === 200 && Array.isArray(response?.data?.result)) {
                    const count = Number(response?.data?.total_count ?? 0);
                    setFinanceProduct(response.data.result || []);
                    setProductPage(page);
                    setProductTotalPages(Math.max(1, Math.ceil(count / productLimit)));
                    calculateTotals(response.data.result);

                } else {
                    toast.error(response?.data?.message || "No finance report found");
                    setFinanceProduct([]);
                    setProductPage(1);
                    setProductTotalPages(1);
                }
            } catch (error) {
                console.error("Error fetching product details:", error);
                toast.error("Failed to fetch product details");
                setFinanceProduct([]);
                setProductPage(1);
                setProductTotalPages(1);
            }
        };
        const getProductPageNumbers = () => {
            const pages = [];
            for (let i = 1; i <= productTotalPages; i++) pages.push(i);
            return pages;
        };

        const handlePageChange = (page) => {
            if (page < 1) return;

            if (radioFilter === "product") {
                if (page > categoryTotalPages) return;
                getFinanceProductId(page);
            } else {
                if (page > categoryTotalPages) return;
                getFinanceReport(page);
            }
        };
        const handleModalPageChange = (pg) => {
            if (!productId) return;
            if (pg < 1 || pg > productTotalPages) return;
            getFinanceProductByCategory(productId, pg);
        };

        useEffect(() => {
            bindGetTaxtRate();
        }, []);

        // useEffect(() => {
        //     if (applyTrigger === 0 && resetTrigger === 0 && !radioFilter) return;
        //     if (radioFilter === "product") {
        //         setCategoryPage(1);
        //         getFinanceProductId(1);
        //     } else {
        //         setCategoryPage(1);
        //         getFinanceReport(1);
        //     }

        // }, [applyTrigger, resetTrigger, radioFilter, filters, selectedCenter, dateTrigger]);

    useEffect(() => {
    if (applyTrigger === 0 && resetTrigger === 0 && !radioFilter) return;

    if (radioFilter === "product") {
        setCategoryPage(1);
        getFinanceProductId(1);
    } else {
        setCategoryPage(1);
        getFinanceReport(1);
    }
}, [applyTrigger, resetTrigger, radioFilter]);


        const downloadCSV = async () => {
            if (totalCount <= 0) {
                toast.info("No data available for download");
                return;
            }
            try {
                let response;
                let sheetName = "";
                let fileName = "";

                const centerList = Array.isArray(selectedCenter)
                    ? selectedCenter.join(",")
                    : selectedCenter;

                const payloadData = {
                    start_date: filters?.startDate || formattedPastDate,
                    end_date: filters?.endDate || formattedToday,
                    location_id: centerList && centerList.length > 0 ? centerList : null,
                    limit: totalCount
                };

                if (radioFilter === "product") {
                    response = await API.getProductWiseGstReport(payloadData);
                    sheetName = "ProductReport";
                    fileName = "ProductReport.xlsx";
                } else {
                    response = await API.addFinanceReport(payloadData);
                    sheetName = "ProductCategoryReport";
                    fileName = "ProductCategoryReport.xlsx";
                }
                if (
                    !response?.data?.result ||
                    !Array.isArray(response.data.result) ||
                    response.data.result.length === 0
                ) {
                    toast.error("No data available for download");
                    return;
                }
                const result = response.data.result;

                const worksheet = XLSX.utils.json_to_sheet(result);
                const workbook = XLSX.utils.book_new();

                XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
                XLSX.writeFile(workbook, fileName);

                //  toast.success("Report downloaded successfully!");
            } catch (error) {
                console.error("Excel Download Failed:", error);
                toast.error("Failed to download report");
            }
        };

        useImperativeHandle(ref, () => ({
            downloadCSV,
        }));
        const sortGstKeys = (keys) => {
            return keys.sort((a, b) => {
                const nA = parseFloat(a.replace(/[^\d.]/g, "")) || 0;
                const nB = parseFloat(b.replace(/[^\d.]/g, "")) || 0;
                return nA - nB;
            });
        };

        const getPageNumbers = () => {
            const maxVisible = 10;
            const pageNumbers = [];

            if (categoryTotalPages <= maxVisible) {
                for (let i = 1; i <= categoryTotalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                const left = Math.max(1, categoryPage - 3);
                const right = Math.min(categoryTotalPages, categoryPage + 3);

                if (left > 2) {
                    pageNumbers.push(1, "...");
                } else {
                    for (let i = 1; i < left; i++) {
                        pageNumbers.push(i);
                    }
                }

                for (let i = left; i <= right; i++) {
                    pageNumbers.push(i);
                }

                if (right < categoryTotalPages - 1) {
                    pageNumbers.push("...", categoryTotalPages);
                } else {
                    for (let i = right + 1; i <= categoryTotalPages; i++) {
                        pageNumbers.push(i);
                    }
                }
            }

            return pageNumbers;
        };

        return (
            <div className="modern-table-container">
                <div className="modern-table-header">
                    <div className="modern-table-title">GST Report Details ({totalCount})</div>
                </div>

                <div className="p-4 text-center">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-bordered text-center">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>ID</th>
                                        <th>{radioFilter === "product" ? "Product Name" : "Product Category"}</th>
                                        {radioFilter != "product" && (
                                            <th>Product Count</th>
                                        )}
                                        <th>Net Sale</th>
                                        {finance.length > 0 &&
                                            sortGstKeys(
                                                Object.keys(finance[0]).filter((key) =>
                                                    key.includes("% GST")
                                                )
                                            ).map((key) => <th key={key}>{key}</th>)}

                                        <th>Additional Cess</th>
                                        <th>Gross Sale</th>
                                        <th>SkyCoins Exhausted</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {finance.length > 0 ? (
                                        finance.map((item, index) => (
                                            <tr key={index}>
                                                <td>{(categoryPage - 1) * perPage + index + 1}</td>
                                                <td>
                                                    {radioFilter === "product" ? (
                                                        <span>{item.id}</span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => getFinanceProductByCategory(item.id, 1)}
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#myModalTaxView"
                                                            className="btn btn-link p-0"
                                                            style={{ textDecoration: "underline", color: "blue", cursor: "pointer" }}
                                                        >
                                                            {item.id}
                                                        </button>
                                                    )}
                                                </td>

                                                <td>{item.product_name || item.product_category || "N/A"}</td>

                                                {radioFilter != "product" && (
                                                    <td>{item.product_count}</td>
                                                )}
                                                <td>{item["Net Sale"] ?? 0}</td>

                                                {Object.keys(item)
                                                    .filter((key) => key.includes("% GST"))
                                                    .sort((a, b) => {
                                                        const nA = parseFloat(a) || 0;
                                                        const nB = parseFloat(b) || 0;
                                                        return nA - nB;
                                                    })
                                                    .map((key) => (
                                                        <td key={key}>{item[key] ?? 0}</td>
                                                    ))}
                                                <td>{item["Additional Cess"] ?? 0}</td>
                                                <td>{item["Gross Sale"] ?? 0}</td>
                                                <td>{item["SkyCoins Exhausted"] ?? 0}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="15">No data available</td>
                                        </tr>
                                    )}
                                </tbody>

                                {/* <tfoot>
                                    <tr>
                                        <th></th>
                                        <th></th>
                                        {radioFilter != "product" && (
                                            <th></th>
                                        )}
                                        <th>Total</th>
                                        <th>{columnTotals.netSale.toFixed(2)}</th>
                                        {sortGstKeys(Object.keys(columnTotals.gstTotals)).map((key) => (
                                            <th key={key}>{(columnTotals.gstTotals[key] || 0).toFixed(2)}</th>
                                        ))}

                                        <th>{columnTotals.additionalCess.toFixed(2)}</th>

                                        <th>{columnTotals.grossSale.toFixed(2)}</th>
                                        <th>{columnTotals.skyCoins.toFixed(2)}</th>
                                    </tr>
                                </tfoot> */}
                            </table>

                            <nav>
                                <ul className="pagination justify-content-center mt-4">

                                    {/* Previous */}
                                    <li className={`page-item ${categoryPage === 1 ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handlePageChange(categoryPage - 1)}
                                            disabled={categoryPage === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>

                                    {getPageNumbers().map((page, i) => (
                                        <li
                                            key={i}
                                            className={`page-item ${page === categoryPage ? "active" : ""
                                                } ${page === "..." ? "disabled" : ""}`}
                                        >
                                            {page === "..." ? (
                                                <span className="page-link">...</span>
                                            ) : (
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            )}
                                        </li>
                                    ))}

                                    {/* Next */}
                                    <li className={`page-item ${categoryPage === categoryTotalPages ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => handlePageChange(categoryPage + 1)}
                                            disabled={categoryPage === categoryTotalPages}
                                        >
                                            Next
                                        </button>
                                    </li>

                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>


                <section>
                    <div className="modal" id="myModalTaxView" data-bs-backdrop="static">
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Finance Activities</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                                </div>

                                <div className="modal-body">
                                    <div className="table-responsive">
                                        <table className="table table-bordered text-center">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>ID</th>
                                                    <th>Product Name</th>
                                                    <th>Net Sale</th>

                                                    {finance.length > 0 &&
                                                        sortGstKeys(
                                                            Object.keys(finance[0]).filter((key) =>
                                                                key.includes("% GST")
                                                            )
                                                        ).map((key) => <th key={key}>{key}</th>)}
                                                    <th>Additional Cess</th>
                                                    <th>Gross Sale</th>
                                                    <th>SkyCoins Exhausted</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {financeProduct.length > 0 ? (
                                                    financeProduct.map((item, index) => (
                                                        <tr key={item.id || index}>
                                                            <td>{(productPage - 1) * productLimit + index + 1}</td>
                                                            <td>{item.id}</td>
                                                            <td>{item.product_name}</td>
                                                            <td>{item["Net Sale"] ?? 0}</td>

                                                            {Object.keys(item)
                                                                .filter((key) => key.includes("% GST"))
                                                                .sort((a, b) => {
                                                                    const nA = parseFloat(a) || 0;
                                                                    const nB = parseFloat(b) || 0;
                                                                    return nA - nB;
                                                                })
                                                                .map((key) => (
                                                                    <td key={key}>{item[key] ?? 0}</td>
                                                                ))}


                                                            <td>{item["Additional Cess"] ?? 0}</td>
                                                            <td>{item["Gross Sale"] ?? 0}</td>
                                                            <td>{item["SkyCoins Exhausted"] ?? 0}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="11">No data found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <th></th>
                                                    <th></th>
                                                    <th>Total</th>
                                                    <th>{columnTotals.netSale.toFixed(2)}</th>
                                                    {sortGstKeys(Object.keys(columnTotals.gstTotals)).map((key) => (
                                                        <th key={key}>{(columnTotals.gstTotals[key] || 0).toFixed(2)}</th>
                                                    ))}
                                                    <th>{columnTotals.additionalCess.toFixed(2)}</th>
                                                    <th>{columnTotals.grossSale.toFixed(2)}</th>
                                                    <th>{columnTotals.skyCoins.toFixed(2)}</th>
                                                </tr>
                                            </tfoot>
                                        </table>

                                    </div>

                                    <nav>
                                        <ul className="pagination justify-content-center mt-4">
                                            <li className={`page-item ${productPage === 1 ? "disabled" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handleModalPageChange(productPage - 1)}
                                                >
                                                    Previous
                                                </button>
                                            </li>

                                            {getProductPageNumbers().map((pg, i) => (
                                                <li key={i} className={`page-item ${pg === productPage ? "active" : ""}`}>
                                                    <button className="page-link" onClick={() => handleModalPageChange(pg)}>
                                                        {pg}
                                                    </button>
                                                </li>
                                            ))}

                                            <li className={`page-item ${productPage === productTotalPages ? "disabled" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handleModalPageChange(productPage + 1)}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
);

export default ProductCategory;
